import React, {
  useCallback, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Drawer, Form, Row, message,
} from 'antd';
import PropTypes from 'prop-types';

import Permissions from '../auth/Permissions';

import DrawerFooter from '../common/containers/DrawerFooter';
import DrawerArchiveFooter from '../common/containers/DrawerArchiveFooter';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

import MaterialDrawerTabContainer from './MaterialDrawerTabContainer';

import {
  getMarkupPercent,
  getDerivedPriceValue,
  getUpdatedPricingFields,
  getComponentsBlockingDivisions,
} from './materialsHelpers';

import { haveSameDivisions, isInDivisions, toDivisionIds } from '../helpers/divisionHelpers';

const SMALL_WIDTH = 800;
const LARGE_WIDTH = '80%';
const LAST_UPLOAD_STEPS = 2;

const removePriceFields = (values) => {
  const { price, labourPrice, ...rest } = values;
  return rest;
};

export default function MaterialDrawer({
  item,
  defaultLocation,
  visible,
  onClose,
  onSubmit,
  isNotDisplay,
  onEdit,
  onArchive,
  onDelete,
  onMassUpload,
  isAssembly,
  divisionFilter,
}) {
  const {
    name,
    divisionIds: initialDivisionIds,
    markup,
    labourMarkup,
    quantity,
    quantityAllocated,
    active,
    locations: initialLocations = defaultLocation,
    isAssembly: isAssemblyItem,
    assemblyMaterials: initialAssemblyMaterials,
    shouldAutoCalcCost: initialAutoCalcCost,
    shouldAutoCalcLabourCost: initialAutoCalcLabourCost,
  } = item || {};

  const isAssemblyState = isAssembly || isAssemblyItem;

  const type = isAssemblyState ? 'Assembly' : 'Material';
  let title = `Add ${type}`;
  if (name) {
    title = isNotDisplay ? `Edit ${type}` : name;
  }
  const [form] = Form.useForm();

  const materials = useSelector((state) => state.materials.materials);
  const vendors = useSelector((state) => state.vendors.vendors);
  const divisionMap = useSelector((state) => state.settings.divisions);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);

  const [selectedDivisions, setSelectedDivisions] = useState(initialDivisionIds ?? []);
  const [locations, setLocations] = useState(initialLocations || defaultLocation);
  const [totalQuantity, setTotalQuantity] = useState(quantity);
  const [totalQuantityAllocated, setTotalQuantityAllocated] = useState(quantityAllocated ?? 0);
  const [activeTab, setActiveTab] = useState('create');
  const [uploadFile, setUploadFile] = useState();
  const [currentStep, setCurrentStep] = useState(0);
  const [massUploadData, setMassUploadData] = useState();
  const [loading, setLoading] = useState(false);
  const [assemblyMaterials, setAssemblyMaterials] = useState(initialAssemblyMaterials || []);
  const [shouldAutoCalcCost, setShouldAutoCalcCost] = useState(initialAutoCalcCost ?? true);
  const [
    shouldAutoCalcLabourCost,
    setShouldAutoCalcLabourCost,
  ] = useState(initialAutoCalcLabourCost ?? true);
  const isCreate = activeTab === 'create';
  const width = isCreate || !uploadFile ? SMALL_WIDTH : LARGE_WIDTH;

  const onSubmitClicked = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const filteredValues = removePriceFields(values); // backend doesn't need price fields
      const filteredAssemblyMaterials = assemblyMaterials?.filter(({ materialId }) => !!materialId);

      onSubmit({
        locations,
        values: filteredValues,
        shouldAutoCalcCost,
        shouldAutoCalcLabourCost,
        assemblyMaterials: filteredAssemblyMaterials,
        isAssembly: isAssemblyState,
      });
    } catch (err) {
      // Failed
    }
  }, [
    onSubmit,
    locations,
    shouldAutoCalcCost,
    shouldAutoCalcLabourCost,
    assemblyMaterials,
    isAssemblyState,
  ]);

  const onValuesChange = useCallback((changedValues, allValues) => {
    Object.keys(changedValues).forEach((key) => {
      if (key === 'cost' && !changedValues.isAutoCalcCost) {
        setShouldAutoCalcCost(false);
      }

      if (key === 'labourCost' && !changedValues.isAutoCalcLabourCost) {
        setShouldAutoCalcLabourCost(false);
      }
    });

    const pricingUpdates = {
      ...getUpdatedPricingFields({
        changedValues,
        allValues,
        priceKey: 'price', // these are defaults, just here for clarity
        markupKey: 'markup',
        costKey: 'cost',
      }),
      ...getUpdatedPricingFields({
        changedValues,
        allValues,
        priceKey: 'labourPrice',
        markupKey: 'labourMarkup',
        costKey: 'labourCost',
      }),
    };
    if (Object.keys(pricingUpdates).length) {
      form.setFieldsValue(pricingUpdates);
    }
  }, [form]);

  const onBack = useCallback(() => setCurrentStep(currentStep - 1), [currentStep]);
  const onNext = useCallback(async () => {
    if (isCreate) {
      onSubmitClicked();
    } else if (currentStep < LAST_UPLOAD_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      if (!selectedDivisions.length) {
        message.error('Please select at least one division');
        return;
      }
      setLoading(true);

      const filteredUpload = [];
      massUploadData.forEach((mat) => {
        if (!mat.name) return;
        const strName = mat.name.toString();
        const newData = {
          ...mat,
          name: strName,
          description: mat.description ? mat.description.toString() : null,
          partNumber: mat.partNumber ? mat.partNumber.toString() : undefined,
          markup: mat.markup && !Number.isNaN(mat.markup) ? mat.markup : undefined,
          supplier: mat.supplier ? mat.supplier.toString() : undefined,
          cost: mat.cost && !Number.isNaN(mat.cost) ? mat.cost : undefined,
          units: mat.units ? mat.units.toString() : undefined,
          quantity: mat.quantity && !Number.isNaN(mat.quantity) ? mat.quantity : 0,
          quantityAllocated: mat.quantityAllocated && !Number.isNaN(mat.quantityAllocated)
            ? mat.quantityAllocated
            : 0,
          divisionIds: selectedDivisions,
        };
        delete newData.id;
        filteredUpload.push(newData);
      });

      await onMassUpload(filteredUpload);
      setLoading(false);
    }
  }, [
    isCreate,
    currentStep,
    onSubmitClicked,
    massUploadData,
    onMassUpload,
    selectedDivisions,
  ]);

  const onDivisionChange = useCallback(async (newDivisions) => {
    const newDivisionIds = toDivisionIds(newDivisions);
    if (haveSameDivisions(newDivisionIds, selectedDivisions)) return;

    // An assembly may only live in divisions that every one of its components lives in.
    if (isAssemblyState) {
      const blockingComponents = getComponentsBlockingDivisions({
        divisionIds: newDivisionIds,
        assemblyMaterials,
        materialsMap: materials,
        divisionMap,
      });
      if (blockingComponents.length) {
        const [{ name: componentName, missingDivisionNames }] = blockingComponents;
        message.error(
          `Cannot add ${missingDivisionNames.join(', ')}: `
          + `${componentName} does not belong to ${missingDivisionNames.length > 1 ? 'those divisions' : 'that division'}.`,
        );
        form.setFieldsValue({ divisionIds: selectedDivisions });
        return;
      }
    }

    const newDivisionIdSet = new Set(newDivisionIds);
    const values = await form.getFieldsValue();
    const newValues = { ...values };

    // Costcodes belong to a single division. Only clear one once we can positively tell
    // it no longer shares a division with the material.
    const currentCostcode = values.costcodeId ? costcodeIdMap[values.costcodeId] : null;
    if (currentCostcode && !newDivisionIdSet.has(currentCostcode.divisionId)) {
      newValues.costcodeId = null;
    }

    const currentVendorId = values.vendorId;
    if (currentVendorId && !isInDivisions(vendors[currentVendorId], newDivisionIdSet)) {
      newValues.vendorId = null;
    }

    await form.setFieldsValue(newValues);
    setSelectedDivisions(newDivisionIds);
  }, [
    selectedDivisions,
    vendors,
    form,
    costcodeIdMap,
    isAssemblyState,
    assemblyMaterials,
    materials,
    divisionMap,
  ]);

  useEffect(() => {
    const {
      divisionIds: itemDivisionIds = [],
      cost: materialCost,
      markup: materialMarkup,
      labourCost,
      labourMarkup: nextLabourMarkup,
    } = item || {};
    setSelectedDivisions(itemDivisionIds);

    const nextPrice = getDerivedPriceValue(materialCost, materialMarkup);
    const nextLabourPrice = getDerivedPriceValue(labourCost, nextLabourMarkup);
    form.setFieldsValue({
      price: nextPrice,
      labourPrice: nextLabourPrice,
    });
  }, [item, form]);

  useEffect(() => {
    if (visible) {
      setShouldAutoCalcCost(initialAutoCalcCost ?? true);
      setShouldAutoCalcLabourCost(initialAutoCalcLabourCost ?? true);
    }

    if (initialAssemblyMaterials && visible) {
      setAssemblyMaterials(initialAssemblyMaterials);
    }

    if (initialDivisionIds?.length && visible) {
      setSelectedDivisions(initialDivisionIds);
    }

    if (initialLocations && visible) {
      setLocations(initialLocations);
    }
  }, [item, visible]);

  useEffect(() => {
    const resetInitialState = async () => {
      setSelectedDivisions([]);
      setLocations(defaultLocation);
      setAssemblyMaterials([]);
      await form.resetFields();
      setActiveTab('create');
      setUploadFile();
      setCurrentStep(0);
      setShouldAutoCalcCost(true);
      setShouldAutoCalcLabourCost(true);
    };
    if (!visible) {
      resetInitialState();
    }
  }, [visible]);

  useEffect(() => {
    if (!locations?.length && quantity) return;

    setTotalQuantity(
      locations.reduce((acc, { quantity: locQuant = 0 }) => (
        acc + (locQuant ? parseFloat(locQuant) : 0)
      ), 0),
    );

    setTotalQuantityAllocated(
      locations.reduce((acc, { quantityAllocated: locQuant = 0 }) => (
        acc + (locQuant ? parseFloat(locQuant) : 0)
      ), 0),
    );
  }, [locations, quantity]);

  useEffect(() => {
    if (
      !form
      || !visible
      || !isAssemblyState
      || (!shouldAutoCalcCost && !shouldAutoCalcLabourCost)
    ) return;

    const newFormValues = {};
    if (shouldAutoCalcCost) newFormValues.cost = 0;
    if (shouldAutoCalcLabourCost) newFormValues.labourCost = 0;

    assemblyMaterials.forEach(({ materialId, quantity: assemblyQuantity = 0 }) => {
      const relevantMaterial = materials[materialId];
      if (!relevantMaterial) return;

      if (shouldAutoCalcCost) {
        if (relevantMaterial) {
          newFormValues.cost += (relevantMaterial.cost || 0) * assemblyQuantity;
        }
      }

      if (shouldAutoCalcLabourCost) {
        if (relevantMaterial) {
          newFormValues.labourCost += (relevantMaterial.labourCost || 0) * assemblyQuantity;
        }
      }
    });

    form.setFieldsValue({
      ...newFormValues,
      isAutoCalcCost: shouldAutoCalcCost,
      isAutoCalcLabourCost: shouldAutoCalcLabourCost,
    });
  }, [
    visible,
    assemblyMaterials,
    form,
    shouldAutoCalcCost,
    shouldAutoCalcLabourCost,
    materials,
    isAssemblyState,
  ]);

  return (
    <Drawer
      title={title}
      width={width}
      visible={visible}
      onClose={onClose}
      // contentWrapperStyle Does not work on our current antd version (4.5.2).
      // Need to upgrade to 4.13.0+
      contentWrapperStyle={{
        transition: 'width 0.5s',
        width,
      }}
    >
      <Form
        layout="vertical"
        form={form}
        className={Permissions.hasWrite('MATERIALS') ? 'materials-write-form' : 'materials-readonly-form'}
        initialValues={{
          ...item,
          markup: getMarkupPercent(markup),
          labourMarkup: getMarkupPercent(labourMarkup),
          price: getDerivedPriceValue(item?.cost, markup),
          labourPrice: getDerivedPriceValue(item?.labourCost, labourMarkup),
        }}
        onValuesChange={onValuesChange}
      >
        <MaterialDrawerTabContainer
          isAdd
          isNotDisplay={isNotDisplay}
          item={item}
          totalQuantity={totalQuantity}
          totalQuantityAllocated={totalQuantityAllocated}
          locations={locations}
          onLocationsChanged={setLocations}
          divisionIds={selectedDivisions}
          divisionFilter={divisionFilter}
          onDivisionChange={onDivisionChange}
          onTabChange={setActiveTab}
          activeTab={activeTab}
          uploadFile={uploadFile}
          onFileChange={setUploadFile}
          currentStep={currentStep}
          onMassUploadDataChanged={setMassUploadData}
          visible={visible}
          isAssembly={isAssemblyState}
          assemblyMaterials={assemblyMaterials}
          onMaterialsChanged={setAssemblyMaterials}
          shouldAutoCalcCost={shouldAutoCalcCost}
          shouldAutoCalcLabourCost={shouldAutoCalcLabourCost}
        />
      </Form>
      {isNotDisplay
        && (
        <DrawerFooter>
          <Row justify="end" align="middle" gutter={8}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              id="customer-add-project-footer-cancel"
              onClick={onClose}
            />
            {currentStep > 0
              && (
              <OnTraccrButton
                title="Back"
                type="back"
                id="customer-add-project-footer-back"
                onClick={onBack}
              />
              )}
            <OnTraccrButton
              title={currentStep < LAST_UPLOAD_STEPS && !isCreate ? 'Next' : 'Submit'}
              onClick={onNext}
              disabled={!isCreate && !uploadFile}
              loading={loading}
            />
          </Row>
        </DrawerFooter>
        )}
      {
        !isNotDisplay && Permissions.hasWrite('MATERIALS')
          && (
          <DrawerArchiveFooter
            active={active}
            onEdit={onEdit}
            onArchive={onArchive}
            onDelete={onDelete}
          />
          )
      }
    </Drawer>
  );
}

MaterialDrawer.propTypes = {
  item: PropTypes.shape({
    cost: PropTypes.number,
    labourCost: PropTypes.number,
  }),
  defaultLocation: PropTypes.arrayOf(PropTypes.shape({})),
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isNotDisplay: PropTypes.bool,
  onEdit: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onMassUpload: PropTypes.func,
  isAssembly: PropTypes.bool,
  divisionFilter: PropTypes.instanceOf(Set),
};

MaterialDrawer.defaultProps = {
  item: {},
  defaultLocation: [],
  visible: false,
  onClose: () => {},
  onSubmit: () => {},
  isNotDisplay: false,
  onEdit: () => {},
  onArchive: () => {},
  onDelete: () => {},
  onMassUpload: () => {},
  isAssembly: false,
  divisionFilter: undefined,
};
